import {
  WebhookIcon, ShieldCheckIcon, ScrollTextIcon, ShieldAlertIcon, PaletteIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ConsoleUsers } from '@/features/settings/console-users';
import { ApiKeys } from '@/features/settings/api-keys';
import { useCapabilityMatrix } from '@/lib/permissions';
import type { Role } from '@/lib/api';
import { CataloguePortability } from '@/features/settings/catalogue-portability';
import { ChangeLog } from '@/features/settings/change-log';
import { useTheme } from '@/app/theme-provider';
import { useT, type Translate } from '@/lib/i18n';
import type { StringKey } from '@/lib/strings/en';
import { THEME_PRESETS, type ThemeMode, type ThemePreset } from '@/lib/themes';

/**
 * Settings & Administration.
 *
 * The walkthrough puts Console users, RBAC, API keys, Webhooks and Integration
 * endpoints on one screen, so they are tabs here rather than separate routes —
 * user management and API-key management live inside this screen, as designed.
 */

/** Header-only table: shows what will be tracked without inventing rows. */
function SchemaTable({ columns }: { columns: StringKey[] }) {
  const t = useT();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((c) => (
            <TableHead key={c}>{t(c)}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell colSpan={columns.length} className="text-muted-foreground h-16 text-center text-sm">
            {t('settings.noRecords')}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

/** Swatch + name, shared by the trigger's selected value and the option list. */
function PresetOption({ preset }: { preset: (typeof THEME_PRESETS)[number] }) {
  return (
    <span className="flex items-center gap-2">
      <span
        aria-hidden
        className="size-3.5 shrink-0 rounded-full border"
        style={{ backgroundColor: preset.swatch }}
      />
      {preset.name}
    </span>
  );
}

/**
 * Appearance — the console's theme controls.
 *
 * This is the whole theme UI: the kit's floating customizer panel is not
 * ported, so there is exactly one place to change how the console looks.
 *
 * Preference only — stored in this browser's localStorage, never sent to the
 * API and not part of any user record. It therefore needs no save button and
 * no Phase-4 caveat: it already works.
 */
function AppearanceTab() {
  const t = useT();
  const { mode, setMode, preset, setPreset } = useTheme();
  const active = THEME_PRESETS.find((t) => t.value === preset) ?? THEME_PRESETS[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PaletteIcon className="size-4" />
          {t('settings.tab.appearance')}
        </CardTitle>
        <CardDescription>{t('settings.appearance.description')}</CardDescription>
      </CardHeader>

      <CardContent className="grid max-w-xl gap-6">
        <div className="grid gap-2">
          <Label htmlFor="theme-preset">{t('profile.theme.preset')}</Label>
          <Select value={preset} onValueChange={(v) => setPreset(v as ThemePreset)}>
            <SelectTrigger id="theme-preset" className="w-full">
              <SelectValue aria-label={active.name}>
                <PresetOption preset={active} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {THEME_PRESETS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  <PresetOption preset={t} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">{t('settings.theme.hint')}</p>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="theme-mode">{t('profile.theme.mode')}</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as ThemeMode)}>
            <SelectTrigger id="theme-mode" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">{t('profile.theme.light')}</SelectItem>
              <SelectItem value="dark">{t('profile.theme.dark')}</SelectItem>
              <SelectItem value="system">{t('settings.theme.matchSystem')}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">{t('settings.mode.hint')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Role permissions — GENERATED from `GET /api/auth/capabilities`.
 *
 * This was a six-row array of booleans written here by hand, and it rendered a
 * **Psychometrician** column for three phases while `Role` held only `admin` and
 * `analyst`. Nobody could be assigned the role the table described. That is what
 * a second copy of a policy does: not disagree loudly, but describe a system
 * that does not exist, convincingly, on the screen an operator goes to in order
 * to find out how the system works.
 *
 * So there is no table here now. The rows are the server's capabilities, the
 * columns are the server's roles, and a tick means `ROLE_CAPABILITIES` says so —
 * the same object `requireCapability` resolves against on every request. Adding
 * a role or a capability changes this screen with no edit to this file, which is
 * the property that stops it lying again.
 */
function RbacTable() {
  const t = useT();
  const { data, isLoading, error } = useCapabilityMatrix();

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  // No invented fallback. A table of ticks rendered from nothing would be the
  // hand-written array again, wearing an error state.
  if (error || !data) {
    return (
      <p className="text-muted-foreground text-sm">
        {error instanceof Error ? error.message : t('table.errorDescription')}
      </p>
    );
  }

  const roles = Object.keys(data.roles) as Role[];

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('settings.column.capability')}</TableHead>
          {roles.map((role) => (
            <TableHead key={role} className="text-center">
              {t(`role.${role}` as StringKey)}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.capabilities.map((capability) => (
          <TableRow key={capability}>
            <TableCell className="font-medium">
              {t(`capability.${capability}` as StringKey)}
            </TableCell>
            {roles.map((role) => {
              const allowed = data.roles[role].includes(capability);
              return (
                <TableCell key={role} className="text-center">
                  {/* Never colour alone, and never a bare glyph: the tick is
                      decorative and the state is read out by the label. */}
                  <span className={allowed ? 'text-foreground' : 'text-muted-foreground'}>
                    <span aria-hidden>{allowed ? '✓' : '—'}</span>
                    <span className="sr-only">
                      {t(allowed ? 'common.yes' : 'common.no')}
                    </span>
                  </span>
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function Settings() {
  const t: Translate = useT();
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('settings.title')}
        description={t('settings.description')}
      />

      <Tabs defaultValue="appearance">
        <TabsList className="flex-wrap">
          {/* Appearance, Console users, Catalogue, Role permissions, API keys
              and the Change log are real. Webhooks and Integration endpoints are
              the two that are not, and they do NOT share a phase with anything:
              both are waiting on the 08 integration strand's delivery half (G6),
              not on a numbered milestone. A single "Phase 4" chip on the header
              once claimed otherwise, which is why there is no chip. */}
          <TabsTrigger value="appearance">{t('settings.tab.appearance')}</TabsTrigger>
          <TabsTrigger value="users">{t('settings.tab.users')}</TabsTrigger>
          <TabsTrigger value="catalogue">{t('settings.tab.catalogue')}</TabsTrigger>
          <TabsTrigger value="rbac">{t('settings.tab.rbac')}</TabsTrigger>
          <TabsTrigger value="api-keys">{t('settings.tab.apiKeys')}</TabsTrigger>
          <TabsTrigger value="webhooks">{t('settings.tab.webhooks')}</TabsTrigger>
          <TabsTrigger value="changelog">{t('settings.tab.changelog')}</TabsTrigger>
          <TabsTrigger value="audit">{t('settings.tab.audit')}</TabsTrigger>
        </TabsList>

        {/* -------------------------------------------------- change log */}
        <TabsContent value="changelog" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.tab.changelog')}</CardTitle>
              <CardDescription>{t('settings.changelog.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ChangeLog />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------------------------------------------- appearance */}
        <TabsContent value="appearance" className="mt-4">
          <AppearanceTab />
        </TabsContent>

        {/* ------------------------------------------------------ users */}
        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.tab.users')}</CardTitle>
              <CardDescription>{t('settings.users.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ConsoleUsers />
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------------------------------------------------- catalogue */}
        <TabsContent value="catalogue" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.catalogue.title')}</CardTitle>
              <CardDescription>{t('settings.catalogue.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <CataloguePortability />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------------------------------------------------- rbac */}
        <TabsContent value="rbac" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheckIcon className="size-4" />
                {t('settings.rbac.title')}
              </CardTitle>
              <CardDescription>{t('settings.rbac.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <RbacTable />
              <p className="text-muted-foreground mt-3 text-xs">{t('settings.rbac.note')}</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --------------------------------------------------- api keys */}
        <TabsContent value="api-keys" className="mt-4 space-y-4">
          <Card className="border-amber-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldAlertIcon className="size-4 text-amber-600 dark:text-amber-500" />
                {t('settings.keys.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-1.5 text-sm">
              <p>{t('settings.keys.storage')}</p>
              <p>{t('settings.keys.scopes')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('settings.tab.apiKeys')}</CardTitle>
              <CardDescription>{t('settings.apiKeys.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <ApiKeys />
            </CardContent>
          </Card>
        </TabsContent>

        {/* --------------------------------------------------- webhooks */}
        <TabsContent value="webhooks" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.tab.webhooks')}</CardTitle>
              <CardDescription>{t('settings.webhooks.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <EmptyState
                icon={WebhookIcon}
                title={t('settings.webhooks.empty.title')}
                description={t('settings.webhooks.empty.description')}
                className="py-10"
              />
              <div className="mt-4 opacity-60">
                <SchemaTable
                  columns={[
                    'settings.column.endpoint', 'settings.column.event',
                    'settings.column.delivery', 'questions.column.status',
                  ]}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ------------------------------------------------------ audit */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScrollTextIcon className="size-4" />
                {t('settings.audit.title')}
              </CardTitle>
              <CardDescription>{t('settings.audit.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <EmptyState
                icon={ScrollTextIcon}
                title={t('settings.audit.empty.title')}
                description={t('settings.audit.empty.description')}
                className="py-10"
              />
              <div className="mt-4 opacity-60">
                <SchemaTable
                  columns={[
                    'settings.column.timestamp', 'settings.column.actor',
                    'settings.column.category', 'settings.column.event',
                    'settings.column.sourceIp',
                  ]}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
